/**
 * KV-Vorlagen (AP-22 Teil 6, K1).
 *
 * Eine Vorlage ist der **Standard eines Kollektivvertrags**: datierte Wertsätze für die
 * Einstellungen, dazu die Fundstelle je Wert. Sie ist Code und kommt mit dem Release zu
 * allen Mandanten — ein Mandant verändert eine Vorlage nicht, er übersteuert sie mit
 * eigenen Sätzen (`payroll_settings`).
 *
 * Die Rechenformeln bleiben in der Engine. Eine Vorlage wählt und parametriert sie; ein
 * neuer Kollektivvertrag ist eine neue Datei in diesem Ordner, kein Eingriff in die Rechnung.
 */

import type { ZulageBemessung } from '../types'

/**
 * Eine Zulage im Katalog einer Vorlage (AP-22 Teil 6, K3).
 *
 * Der Kern kennt eine feste Menge von Bemessungsarten; eine Vorlage **kombiniert** sie. Ein
 * neuer Kollektivvertrag bringt seine Zulagen als Einträge mit, ohne dass die Engine wächst.
 * Satz, Schalter und — wo nötig — das Uhrzeitfenster sind Einstellungen mit festem Namen
 * (`zulageSchluessel`) und damit versioniert und je Betrieb übersteuerbar wie jeder andere Wert.
 */
export interface ZulageDef {
  /** Stabile Kennung — in Einstellungsschlüsseln, Tageseingaben und Ergebnissen. */
  schluessel: string
  name: string
  /** Kurz, für den Spaltenkopf. */
  kurz: string
  fundstelle: string
  bemessung: ZulageBemessung
  /** Einheit der Menge. */
  einheit: 'h' | 'Nacht' | 'km'
  /** Der Satz ist ein Prozentsatz vom Lohn — ausgewiesen wird nur die Menge. */
  prozent?: boolean
  /** Entfällt, soweit diese andere Zulage am selben Tag gebührt. */
  entfaelltBei?: string
  /** Ein Satz für die Maske: wann die Zulage gebührt. */
  hinweis?: string
}

/** Die Einstellungsschlüssel einer Zulage. */
export function zulageSchluessel(schluessel: string): { aktiv: string; satz: string; von: string; bis: string } {
  return {
    aktiv: `zulage_${schluessel}_aktiv`,
    satz: `zulage_${schluessel}_satz`,
    von: `zulage_${schluessel}_von`,
    bis: `zulage_${schluessel}_bis`,
  }
}

/** Werte, die ab einem Tag gelten. Spätere Sätze tragen nur, was sich ändert. */
export interface KvWertsatz {
  /** ISO-Datum (YYYY-MM-DD). */
  gueltigAb: string
  /** Schlüssel des Einstellungskatalogs → Wert als Text, wie in `payroll_settings`. */
  werte: Record<string, string>
}

export interface KvVorlage {
  /** Stabile Kennung, gespeichert am Mandanten bzw. an der Entlohnungsart. */
  schluessel: string
  /** Ausgeschrieben, für die Auswahl. */
  name: string
  /** Kurz, für „KV-Standard (…)". */
  kurz: string
  /** Welche Fassung des Vertrags eingearbeitet ist. */
  fassung: string
  /** Woher der Wortlaut stammt — zum Gegenprüfen. */
  quelle: string
  /**
   * Aufsteigend nach `gueltigAb`. Der **erste** Satz trägt jeden Schlüssel des
   * Einstellungskatalogs samt denen der eigenen Zulagen (`einstellungskatalogFuer`) und gilt
   * von Anfang an — eine Vorlage lässt keinen Wert offen.
   */
  wertsaetze: readonly KvWertsatz[]
  /** Schlüssel → Fundstelle im Vertrag oder Gesetz (z. B. „VIII/4"). */
  fundstellen: Readonly<Record<string, string>>
  /** Der Zulagenkatalog des Vertrags, in der Reihenfolge von Maske und Raster. */
  zulagen: readonly ZulageDef[]
}
